import React, { useState } from 'react';
import mammoth from 'mammoth';
import { Upload, FileText, Download, AlertCircle, CheckCircle } from 'lucide-react';

const WordToJsonParser = () => {
  const [jsonOutput, setJsonOutput] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState('');

  const extractImagesAsBase64 = async (arrayBuffer) => {
    const images = {};
    let imageIndex = 0;
    
    const options = {
      convertImage: mammoth.images.imgElement(function(image) {
        return image.read("base64").then(function(imageBuffer) {
          const imageId = `image_${imageIndex++}`;
          const contentType = image.contentType || 'image/png';
          images[imageId] = `data:${contentType};base64,${imageBuffer}`;
          
          // Return an img element with the temporary ID
          return {
            src: imageId,
            alt: `Image ${imageIndex}`
          };
        });
      })
    };
    
    const result = await mammoth.convertToHtml({ arrayBuffer }, options);
    console.log('Extracted images:', Object.keys(images).length);
    return { html: result.value, images };
  };
  
  const extractLinks = (element) => {
    const links = [];
    
    // Extract hyperlinks (anchor tags)
    const anchorElements = element.querySelectorAll('a');
    anchorElements.forEach((anchor) => {
      const href = anchor.getAttribute('href');
      const text = anchor.textContent.trim();
      if (href) {
        links.push({
          url: href,
          text: text || href
        });
      }
    });
    
    // Extract plain URLs from text
    const text = element.textContent;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const plainUrls = text.match(urlRegex) || [];
    
    // Add plain URLs that aren't already captured as hyperlinks
    plainUrls.forEach((url) => {
      const isAlreadyCaptured = links.some(link => link.url === url);
      if (!isAlreadyCaptured) {
        links.push({
          url: url,
          text: url
        });
      }
    });
    
    return links;
  };

  const parseWordDocument = async (file) => {
    try {
      setLoading(true);
      setError(null);
      
      const arrayBuffer = await file.arrayBuffer();
      const { html, images } = await extractImagesAsBase64(arrayBuffer);
      
      console.log('HTML output:', html.substring(0, 500)); // Debug
      console.log('Images found:', images); // Debug
      
      // Create a temporary DOM element to parse HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const paragraphs = doc.querySelectorAll('p');
      
      // Initialize packet structure
      const packet = {
        name: '',
        league: '',
        date: '',  // Empty by default, will extract from document
        questionsCount: 0,
        authors: [],
        questions: []
      };
      
      let currentQuestion = null;
      let imageCounter = 1;
      const authorsSet = new Set();
      let inSourceSection = false;
      
      // Parse paragraphs
      paragraphs.forEach((p) => {
        const text = p.textContent.trim();
        
        // Check for images in current paragraph
        const imgElements = p.querySelectorAll('img');
        
        // Check for packet metadata (looking for tournament/league patterns)
        if (text.includes('ტურნირი') || text.includes('საავტორო')) {
          packet.name = text;
        } else if (text.includes('ლიგა') || text.includes('ტური')) {
          packet.league = text;
        } 
        // Check for date patterns
        else if (text.includes('თარიღი:') || text.includes('თარიღი')) {
          // Extract date from various formats
          const dateText = text.replace(/თარიღი:?/, '').trim();
          
          // Try to parse different date formats
          // Format: 2024-12-15 or 15/12/2024 or 15.12.2024 or 15-12-2024
          const datePatterns = [
            /(\d{4})-(\d{1,2})-(\d{1,2})/, // YYYY-MM-DD
            /(\d{1,2})\/(\d{1,2})\/(\d{4})/, // DD/MM/YYYY
            /(\d{1,2})\.(\d{1,2})\.(\d{4})/, // DD.MM.YYYY
            /(\d{1,2})-(\d{1,2})-(\d{4})/  // DD-MM-YYYY
          ];
          
          for (const pattern of datePatterns) {
            const match = dateText.match(pattern);
            if (match) {
              if (match[1].length === 4) {
                // YYYY-MM-DD format
                packet.date = `${match[1]}-${match[2].padStart(2, '0')}-${match[3].padStart(2, '0')}`;
              } else {
                // DD/MM/YYYY or similar formats
                packet.date = `${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
              }
              console.log('Extracted date:', packet.date);
              break;
            }
          }
          
          // If no pattern matched, store the raw text
          if (!packet.date && dateText) {
            packet.date = dateText;
          }
        }
        // Check for question start (numbered pattern like "1.", "2.", etc.)
        else if (/^\d+\./.test(text)) {
          if (currentQuestion) {
            packet.questions.push(currentQuestion);
          }
          inSourceSection = false;
          currentQuestion = {
            number: packet.questions.length + 1,
            questionText: text.replace(/^\d+\./, '').trim(),
            answer: '',
            source: '',
            sourceLinks: [],
            author: '',
            images: []
          };
          
          // Check for images in question paragraph
          imgElements.forEach((img) => {
            const imageSrc = img.getAttribute('src');
            if (images[imageSrc]) {
              currentQuestion.images.push({
                id: imageCounter++,
                location: 1, // In question
                base64: images[imageSrc]
              });
              console.log('Added image to question:', imageCounter - 1);
            }
          });
          
          // Check for links in question
          const questionLinks = extractLinks(p);
          if (questionLinks.length > 0) {
            currentQuestion.questionLinks = questionLinks;
          }
        }
        // Parse question fields
        else if (currentQuestion) {
          // Handle images in any paragraph that's part of the current question
          if (imgElements.length > 0 && !text) {
            // This is likely an image-only paragraph
            imgElements.forEach((img) => {
              const imageSrc = img.getAttribute('src');
              if (images[imageSrc]) {
                currentQuestion.images.push({
                  id: imageCounter++,
                  location: inSourceSection ? 2 : 1,
                  base64: images[imageSrc]
                });
                console.log(`Added standalone image, location: ${inSourceSection ? 2 : 1}`);
              }
            });
          }
          
          if (text.startsWith('პასუხი:') || text.includes('პასუხი:')) {
            inSourceSection = false;
            currentQuestion.answer = text.replace(/პასუხი:?/, '').trim();
            
            // Check for links in answer
            const answerLinks = extractLinks(p);
            if (answerLinks.length > 0) {
              currentQuestion.answerLinks = answerLinks;
            }
          } else if (text.startsWith('წყარო:') || text.includes('წყარო:')) {
            inSourceSection = true;
            currentQuestion.source = text.replace(/წყარო:?/, '').trim();
            
            // Extract links from source
            const sourceLinks = extractLinks(p);
            if (sourceLinks.length > 0) {
              currentQuestion.sourceLinks = sourceLinks;
              console.log('Found links in source:', sourceLinks);
            }
            
            // Check for images in source
            imgElements.forEach((img) => {
              const imageSrc = img.getAttribute('src');
              if (images[imageSrc]) {
                currentQuestion.images.push({
                  id: imageCounter++,
                  location: 2, // In source
                  base64: images[imageSrc]
                });
                console.log('Added image to source:', imageCounter - 1);
              }
            });
          } else if (text.startsWith('ავტორი:') || text.includes('ავტორი:')) {
            inSourceSection = false;
            const author = text.replace(/ავტორი:?/, '').trim();
            currentQuestion.author = author;
            authorsSet.add(author);
          } else if (text.startsWith('კომენტარი:') || text.includes('კომენტარი:')) {
            inSourceSection = false;
            currentQuestion.comment = text.replace(/კომენტარი:?/, '').trim();
            
            // Check for links in comment
            const commentLinks = extractLinks(p);
            if (commentLinks.length > 0) {
              currentQuestion.commentLinks = commentLinks;
            }
          } else if (text && !text.includes(':')) {
            // This might be continuation of question text if we're not in a labeled section
            if (!inSourceSection && !currentQuestion.answer) {
              currentQuestion.questionText += ' ' + text;
            }
          }
        }
      });
      
      // Add last question if exists
      if (currentQuestion) {
        packet.questions.push(currentQuestion);
      }
      
      // Finalize packet data
      packet.questionsCount = packet.questions.length;
      packet.authors = Array.from(authorsSet);
      
      // If no packet name found, use file name
      if (!packet.name) {
        packet.name = file.name.replace('.docx', '');
      }
      
      console.log('Final packet:', packet); // Debug
      
      setJsonOutput({ packet });
      setFileName(file.name);
    } catch (err) {
      setError('Error parsing document: ' + err.message);
      console.error('Parsing error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      parseWordDocument(file);
    } else {
      setError('Please upload a valid .docx file');
    }
  };

  const downloadJson = () => {
    if (!jsonOutput) return;
    
    const blob = new Blob([JSON.stringify(jsonOutput, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName.replace('.docx', '.json');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = () => {
    if (!jsonOutput) return;
    navigator.clipboard.writeText(JSON.stringify(jsonOutput, null, 2));
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Word to JSON Parser</h1>
        <p className="text-gray-600 mb-6">Georgian Quiz Document Parser</p>
        
        {/* File Upload Section */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 mb-6 text-center">
          <input
            type="file"
            accept=".docx"
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
          />
          <label htmlFor="file-upload" className="cursor-pointer">
            <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-lg text-gray-700 mb-2">Click to upload Word document</p>
            <p className="text-sm text-gray-500">.docx files only</p>
          </label>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-blue-700">Parsing document...</p>
          </div>
        )}

        {/* Success State & JSON Output */}
        {jsonOutput && (
          <>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
              <span className="text-green-700">
                Successfully parsed {jsonOutput.packet.questionsCount} questions from {fileName}
              </span>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Questions</p>
                <p className="text-2xl font-bold text-gray-800">{jsonOutput.packet.questionsCount}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Authors</p>
                <p className="text-2xl font-bold text-gray-800">{jsonOutput.packet.authors.length}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Images</p>
                <p className="text-2xl font-bold text-gray-800">
                  {jsonOutput.packet.questions.reduce((acc, q) => acc + q.images.length, 0)}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Links</p>
                <p className="text-2xl font-bold text-gray-800">
                  {jsonOutput.packet.questions.reduce((acc, q) => 
                    acc + (q.sourceLinks?.length || 0) + (q.questionLinks?.length || 0) + 
                    (q.answerLinks?.length || 0) + (q.commentLinks?.length || 0), 0)}
                </p>
              </div>
            </div>

            {/* JSON Preview */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-semibold text-gray-700">JSON Output</h3>
                <div className="space-x-2">
                  <button
                    onClick={copyToClipboard}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition"
                  >
                    Copy
                  </button>
                  <button
                    onClick={downloadJson}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition flex items-center inline-flex"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download JSON
                  </button>
                </div>
              </div>
              <pre className="bg-white p-4 rounded border border-gray-200 overflow-x-auto max-h-96 text-sm">
                {JSON.stringify(jsonOutput, null, 2)}
              </pre>
            </div>
          </>
        )}

        {/* Template Instructions */}
        <div className="bg-blue-50 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">Word Template Format:</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li>• Start each question with a number and period (e.g., "1. კითხვის ტექსტი")</li>
            <li>• Use "პასუხი:" for answers</li>
            <li>• Use "წყარო:" for sources</li>
            <li>• Use "ავტორი:" for authors</li>
            <li>• Use "კომენტარი:" for comments (optional)</li>
            <li>• Images in questions will get location ID 1</li>
            <li>• Images in sources will get location ID 2</li>
            <li>• Include packet name with "ტურნირი" and league with "ლიგა"</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default WordToJsonParser;